import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// Session timeout settings
const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour of inactivity
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // Warn 5 minutes before timeout

// Storage keys
const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const REMEMBER_KEY = 'rememberMe';

export function useAuth() {
  return useContext(AuthContext);
}

// Helper to get storage based on remember me preference
const getStorage = () => {
  const rememberMe = localStorage.getItem(REMEMBER_KEY) === 'true';
  return rememberMe ? localStorage : sessionStorage;
};

// Helper to get initial user from cache
const getInitialUser = () => {
  try {
    const rememberMe = localStorage.getItem(REMEMBER_KEY) === 'true';
    const storage = rememberMe ? localStorage : sessionStorage;
    const cachedUser = storage.getItem(USER_KEY);
    const token = storage.getItem(TOKEN_KEY);
    if (cachedUser && token) {
      return JSON.parse(cachedUser);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

export function AuthProvider({ children }) {
  // Initialize user from cache immediately to prevent flash
  const [user, setUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);

  // Update last activity timestamp - throttled
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
    setSessionWarning(false);
  }, []);

  const logout = useCallback((showMessage = true) => {
    // Clear from both storages
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REMEMBER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    
    setUser(null);
    setSessionWarning(false);
    
    if (showMessage) {
      toast.info('You have been logged out');
    }
    
    // Redirect to home page instead of login
    window.location.href = '/';
  }, []);

  const extendSession = useCallback(() => {
    updateActivity();
    setSessionWarning(false);
    toast.success('Session extended for 1 hour');
  }, [updateActivity]);

  const checkAuth = useCallback(async () => {
    // Check both storages for token
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    const rememberMe = localStorage.getItem(REMEMBER_KEY) === 'true';
    
    // If no "remember me" and token only in localStorage, clear it (fresh visit)
    if (!rememberMe && localStorage.getItem(TOKEN_KEY) && !sessionStorage.getItem(TOKEN_KEY)) {
      // This is a fresh browser session without remember me - clear old token
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setLoading(false);
      return;
    }
    
    if (token) {
      // Try to use cached user data first for faster initial load
      const cachedUser = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
      if (cachedUser) {
        try {
          const userData = JSON.parse(cachedUser);
          setUser(userData);
          setLoading(false);
          updateActivity();
          
          // Verify token in background (don't block UI)
          authAPI.getMe().then(response => {
            if (response.data) {
              setUser(response.data);
              // Update cache
              const storage = getStorage();
              storage.setItem(USER_KEY, JSON.stringify(response.data));
            }
          }).catch(() => {
            // Token invalid, clear everything
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            sessionStorage.removeItem(TOKEN_KEY);
            sessionStorage.removeItem(USER_KEY);
            setUser(null);
          });
          return;
        } catch (e) {
          // Invalid cached data, continue with API call
        }
      }
      
      // No cached user, must fetch
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        updateActivity();
      } catch (error) {
        // Clear invalid tokens
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, [updateActivity]);

  // Session timeout check
  useEffect(() => {
    if (!user) return;

    const checkTimeout = () => {
      const inactiveTime = Date.now() - lastActivityRef.current;
      
      if (inactiveTime >= SESSION_TIMEOUT) {
        logout(true);
        return;
      }
      
      const timeUntilTimeout = SESSION_TIMEOUT - inactiveTime;
      if (timeUntilTimeout <= WARNING_BEFORE_TIMEOUT && !warningShownRef.current) {
        warningShownRef.current = true;
        setSessionWarning(true);
        toast.warning('Your session will expire in 5 minutes. Click anywhere to stay logged in.', {
          duration: 10000,
          action: {
            label: 'Extend Session',
            onClick: extendSession
          }
        });
      }
    };

    const intervalId = setInterval(checkTimeout, 30000);
    return () => clearInterval(intervalId);
  }, [user, logout, extendSession]);

  // Activity listeners with throttling
  useEffect(() => {
    if (!user) return;

    let throttleTimeout = null;
    const throttledUpdate = () => {
      if (!throttleTimeout) {
        updateActivity();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, 5000); // Increased throttle to 5 seconds
      }
    };

    // Removed mousemove - too frequent. Using only meaningful interactions
    const events = ['mousedown', 'keydown', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, throttledUpdate, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledUpdate);
      });
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [user, updateActivity]);

  // Initial auth check
  useEffect(() => {
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await authAPI.login({ email, password });
      
      if (!response.data || !response.data.token) {
        throw new Error('Invalid login response');
      }
      
      // Store remember me preference in localStorage (persists)
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, 'true');
        localStorage.setItem(TOKEN_KEY, response.data.token);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
        // Use sessionStorage - cleared when browser closes
        sessionStorage.setItem(TOKEN_KEY, response.data.token);
      }
      
      // Get full user profile
      try {
        const profileResponse = await authAPI.getMe();
        const fullUserData = profileResponse.data;
        
        if (rememberMe) {
          localStorage.setItem(USER_KEY, JSON.stringify(fullUserData));
        } else {
          sessionStorage.setItem(USER_KEY, JSON.stringify(fullUserData));
        }
        
        setUser(fullUserData);
        updateActivity();
        return { ...response.data, user: fullUserData };
      } catch (profileError) {
        // If profile fetch fails, use data from login response
        console.warn('Profile fetch failed, using login data:', profileError);
        const userData = response.data.user;
        if (userData) {
          if (rememberMe) {
            localStorage.setItem(USER_KEY, JSON.stringify(userData));
          } else {
            sessionStorage.setItem(USER_KEY, JSON.stringify(userData));
          }
          setUser(userData);
          updateActivity();
          return response.data;
        }
        throw new Error('Unable to get user profile');
      }
    } catch (error) {
      // Clear any partial data on error
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      throw error;
    }
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    
    // New registrations don't get "remember me" by default
    sessionStorage.setItem(TOKEN_KEY, response.data.token);
    
    try {
      const profileResponse = await authAPI.getMe();
      const fullUserData = profileResponse.data;
      sessionStorage.setItem(USER_KEY, JSON.stringify(fullUserData));
      setUser(fullUserData);
      updateActivity();
      return { ...response.data, user: fullUserData };
    } catch (error) {
      sessionStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
      setUser(response.data.user);
      updateActivity();
      return response.data;
    }
  };

  const refreshUser = async () => {
    try {
      const profileResponse = await authAPI.getMe();
      const userData = profileResponse.data;
      setUser(userData);
      const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
      if (token) {
        if (localStorage.getItem(TOKEN_KEY)) localStorage.setItem(USER_KEY, JSON.stringify(userData));
        else sessionStorage.setItem(USER_KEY, JSON.stringify(userData));
      }
    } catch (e) { /* silent */ }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      refreshUser,
      sessionWarning,
      extendSession 
    }}>
      {children}
      
      {/* Session Warning Banner */}
      {sessionWarning && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-amber-500 text-white p-4 rounded-xl shadow-2xl z-[100] animate-bounce">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold">Session Expiring Soon</p>
              <p className="text-sm text-amber-100">Click anywhere or tap below to stay logged in</p>
            </div>
            <button 
              onClick={extendSession}
              className="bg-white text-amber-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-amber-50 transition-colors"
            >
              Extend
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}
